/**
 * AppHeader — brand-gradient page header used on all in-app screens.
 *
 * Props:
 *   title    — page title (required)
 *   subtitle — optional single line of secondary text below the title (white/65)
 *   showBack — show a back chevron on the left (default false)
 *   onBack   — custom back handler; falls back to navigate(-1)
 *   right    — optional JSX slot for extra action buttons (rendered before bell)
 *              Button colours on the dark gradient should use:
 *              className="tap-target text-white/80 hover:text-white"
 *              or: className="tap-target w-8 h-8 rounded-full bg-white/20 text-white"
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useApp } from '@/context/AppContext'

// ── Relative time formatter ────────────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins  = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days  = Math.floor(diff / 86_400_000)
  if (mins  <  2) return 'just now'
  if (mins  < 60) return `${mins}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days  <  7) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ── Notification type → icon map ──────────────────────────────────────────────
const NOTIF_ICON = {
  message:    '💬',
  calendar:   '📅',
  document:   '📄',
  court:      '⚖️',
  cps:        '🏛️',
  coparent:   '🤝',
  system:     '🔔',
  expense:    '💰',
  default:    '🔔',
}

function notifIcon(type) {
  return NOTIF_ICON[type] || NOTIF_ICON.default
}

// ── Notification drawer ────────────────────────────────────────────────────────
function NotificationDrawer({ open, onClose }) {
  const { notifications, unreadCount, markNotifRead, refreshNotifications } = useApp()
  const navigate = useNavigate()

  // Mark all read when drawer opens
  useEffect(() => {
    if (open && unreadCount > 0) {
      markNotifRead(null) // null = mark all
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh list when drawer opens
  useEffect(() => {
    if (open) refreshNotifications()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleNotifTap = useCallback((notif) => {
    onClose()
    if (notif.link) navigate(notif.link)
  }, [navigate, onClose])

  if (!open) return null

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]"
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div
        className="fixed left-0 right-0 z-50 mx-auto max-w-lg"
        style={{ top: 0, paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div
          className="bg-white dark:bg-slate-800 rounded-b-2xl shadow-2xl overflow-hidden border-x border-b border-transparent dark:border-slate-700"
          style={{ animation: 'slideDown 0.22s cubic-bezier(0.4,0,0.2,1)' }}
        >
          {/* Header row */}
          <div className="brand-gradient px-5 pt-4 pb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xl">🔔</span>
              <h2 className="text-white font-bold text-lg">Notifications</h2>
              {unreadCount > 0 && (
                <span className="bg-white/25 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="tap-target text-white/70 hover:text-white transition-colors"
              aria-label="Close notifications"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Notification list */}
          <div className="max-h-[60vh] overflow-y-auto no-scrollbar divide-y divide-slate-100 dark:divide-slate-700">
            {notifications.length === 0 && (
              <div className="py-10 text-center">
                <span className="text-4xl block mb-2">🎉</span>
                <p className="text-base text-slate-500">You're all caught up!</p>
              </div>
            )}
            {notifications.map(notif => (
              <button
                key={notif.id}
                onClick={() => handleNotifTap(notif)}
                className={[
                  'w-full text-left px-5 py-4 flex items-start gap-3',
                  'hover:bg-slate-50 dark:hover:bg-slate-700 active:bg-slate-100 dark:active:bg-slate-600 transition-colors',
                  !notif.read ? 'bg-brand-50/60 dark:bg-brand-900/20' : '',
                ].join(' ')}
              >
                <span className="text-2xl flex-shrink-0 mt-0.5">{notifIcon(notif.type)}</span>
                <div className="flex-1 min-w-0">
                  {notif.title && (
                    <p className={`text-base leading-snug mb-0.5 ${!notif.read ? 'font-semibold text-slate-800' : 'font-medium text-slate-700'}`}>
                      {notif.title}
                    </p>
                  )}
                  <p className={`text-sm leading-relaxed ${!notif.read ? 'text-slate-800' : 'text-slate-600'}`}>
                    {notif.body || notif.message || notif.text}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">{timeAgo(notif.created_at)}</p>
                </div>
                {!notif.read && (
                  <span className="flex-shrink-0 mt-2 w-2.5 h-2.5 rounded-full bg-brand-500" />
                )}
              </button>
            ))}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-slate-100 dark:border-slate-700 px-5 py-4">
              <button
                onClick={() => { markNotifRead(null); onClose() }}
                className="text-sm text-brand-600 font-semibold hover:text-brand-800 transition-colors"
              >
                Mark all as read
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from { transform: translateY(-100%); opacity: 0; }
          to   { transform: translateY(0);     opacity: 1; }
        }
      `}</style>
    </>
  )
}

// ── AppHeader ─────────────────────────────────────────────────────────────────
export default function AppHeader({ title, subtitle, showBack = false, onBack, right }) {
  const navigate   = useNavigate()
  const { unreadCount, logout } = useApp()
  const [drawerOpen, setDrawerOpen] = useState(false)

  return (
    <>
      <div className="brand-gradient px-5 pb-4 flex-shrink-0" style={{ paddingTop: 'calc(1.25rem + env(safe-area-inset-top, 0px))' }}>
        <div className="flex items-center justify-between gap-3">

          {/* Left — back button + title */}
          <div className="flex items-center gap-2 min-w-0">
            {showBack && (
              <button
                onClick={onBack ?? (() => navigate(-1))}
                className="tap-target flex-shrink-0 -ml-1 text-white/80 hover:text-white transition-colors"
                aria-label="Go back"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} className="w-5 h-5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="min-w-0">
              <h1 className="text-white font-bold text-xl leading-tight truncate">{title}</h1>
              {subtitle && (
                <p className="text-white/65 text-xs mt-0.5 truncate">{subtitle}</p>
              )}
            </div>
          </div>

          {/* Right — custom slot + bell + settings + logout */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {right}

            {/* Notification bell — opens in-app notification drawer */}
            <button
              onClick={() => setDrawerOpen(true)}
              className="relative tap-target text-white/80 hover:text-white transition-colors"
              aria-label="Notifications"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[9px] font-bold flex items-center justify-center text-white">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            <button
              onClick={() => navigate('/settings')}
              className="tap-target text-white/80 hover:text-white transition-colors"
              title="Settings"
              aria-label="Open settings"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>

            <button
              onClick={logout}
              className="tap-target text-white/80 hover:text-white transition-colors"
              title="Sign out"
              aria-label="Sign out"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-6 h-6">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          </div>

        </div>
      </div>

      <NotificationDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </>
  )
}
